import type { ReactNode } from "react";
import React, { createContext, useContext } from "react";

import type { Libraries } from "@react-google-maps/api";
import { useJsApiLoader } from "@react-google-maps/api";

type ContextType = {
    isLoaded: boolean;
    loadError: Error | undefined;
};

const GoogleApiContext = createContext<ContextType | null>(null);

export const useGoogleApi = () => {
    const context = useContext(GoogleApiContext);

    if (!context) {
        throw new Error("useGoogleApi must be used within a GoogleApiProvider");
    }
    return context;
};

interface GoogleApiProviderProps {
    children: ReactNode;
    googleApiKey?: string;
}

const LIBRARIES: Libraries = ["maps", "places", "geometry"];

export const GoogleApiProvider: React.FC<React.PropsWithChildren<GoogleApiProviderProps>> = ({
    children,
    googleApiKey,
}) => {
    const googleMapsApiKey = process.env.NEXT_PUBLIC_DAYTRIP_GOOGLE_MAPS_API_KEY || googleApiKey || "";

    const { isLoaded, loadError } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: googleMapsApiKey,
        libraries: LIBRARIES,
    });

    return <GoogleApiContext.Provider value={{ isLoaded, loadError }}>{children}</GoogleApiContext.Provider>;
};
